import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { authFetch } from '../utils/api';
import { buildSbomCandidates } from '../utils/sbom';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';
import { useToast } from './common/ToastContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS = {
  reachable:            { tone: 'danger',    icon: '⚠️',  label: 'REACHABLE' },
  not_affected:         { tone: 'success',   icon: '✅',  label: 'NOT AFFECTED' },
  under_investigation:  { tone: 'secondary', icon: '🔵',  label: 'UNDER INVESTIGATION' },
  fixed:                { tone: 'primary',   icon: '🔒',  label: 'FIXED' },
};

const TIER = {
  'osv-symbols':  { color: '#10b981', label: 'OSV Symbols',   title: 'Function-level data extracted directly from the OSV advisory (highest quality)' },
  'fix-commit':   { color: '#3b82f6', label: 'Fix Commit',    title: 'Sink functions extracted by parsing the vulnerability fix commit diff on GitHub' },
  'cwe-patterns': { color: '#f59e0b', label: 'CWE Patterns',  title: 'Sink patterns derived from the CWE weakness type (e.g. CWE-89 → SQL Injection sinks)' },
  'import-check': { color: '#8b5cf6', label: 'Import Check',  title: 'Static analysis: checked whether the package is imported in the codebase and whether vulnerable call patterns are present' },
  'semgrep':      { color: '#ef4444', label: 'Semgrep',       title: 'Semgrep OWASP community rule matched a CVE/CWE pattern in the application source. A human-authored rule confirmed the vulnerable API is called in your code.' },
  'unknown':      { color: '#6b7280', label: 'Unknown',       title: 'No function-level data was available for this advisory' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function TierBadge({ tier }) {
  const cfg = TIER[tier] || TIER.unknown;
  return (
    <span
      title={cfg.title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.5px',
        padding: '2px 8px', borderRadius: 20,
        background: cfg.color + '18', color: cfg.color,
        border: `1px solid ${cfg.color}40`,
        cursor: 'help',
      }}
    >
      🔍 {cfg.label}
    </span>
  );
}

function ReachabilityBadge({ status }) {
  const cfg = STATUS[status] || STATUS.under_investigation;
  const sevMap = { danger: 'critical', success: 'low', primary: 'info', secondary: 'none' };
  return (
    <span className={`ce-sev ce-sev--${sevMap[cfg.tone] || 'none'}`}>
      {cfg.icon} {cfg.label}
    </span>
  );
}

/** Vulnerable Path Analysis — vertical call chain visualization */
function VulnerablePathAnalysis({ edges }) {
  const [expanded, setExpanded] = useState(false);
  if (!edges || edges.length === 0) return null;

  const preview = edges.slice(0, 2);
  const shown = expanded ? edges : preview;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
        fontSize: '0.78rem', fontWeight: 700, color: '#ef4444',
        letterSpacing: '0.5px', textTransform: 'uppercase',
      }}>
        🔴 Vulnerable Path Analysis
        {edges.length > 2 && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#6b7280', fontSize: '0.72rem', padding: 0,
            }}
          >
            {expanded ? '▲ collapse' : `▼ show all ${edges.length} hops`}
          </button>
        )}
      </div>

      <div style={{
        background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.18)',
        borderRadius: 8, padding: '10px 14px',
      }}>
        {shown.map((edge, i) => (
          <div key={i}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{
                minWidth: 22, height: 22, borderRadius: '50%',
                background: i === 0 ? '#3b82f620' : i === edges.length - 1 ? '#ef444420' : '#6b728018',
                color: i === 0 ? '#3b82f6' : i === edges.length - 1 ? '#ef4444' : '#6b7280',
                fontSize: '0.65rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 2,
              }}>
                {i === 0 ? 'SRC' : i === edges.length - 1 ? 'SNK' : i + 1}
              </span>
              <div>
                <code style={{ fontSize: '0.75rem', color: 'var(--color-text, #1e293b)' }}>
                  {edge.caller}
                </code>
                {i === shown.length - 1 && edge.callee && (
                  <div style={{ marginTop: 2 }}>
                    <code style={{ fontSize: '0.75rem', color: '#ef4444' }}>
                      → {edge.callee}
                    </code>
                    <span style={{
                      marginLeft: 8, fontSize: '0.65rem', fontWeight: 700,
                      color: '#ef4444', background: '#ef444415',
                      padding: '1px 6px', borderRadius: 4,
                    }}>VULNERABLE</span>
                  </div>
                )}
              </div>
            </div>
            {i < shown.length - 1 && (
              <div style={{ marginLeft: 11, borderLeft: '1px dashed #6b728040', height: 12 }} />
            )}
          </div>
        ))}

        {!expanded && edges.length > 2 && (
          <div style={{ marginLeft: 11, borderLeft: '1px dashed #6b728040', height: 12 }} />
        )}
        {!expanded && edges.length > 2 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 2 }}>
            <span style={{
              minWidth: 22, height: 22, borderRadius: '50%',
              background: '#ef444420', color: '#ef4444',
              fontSize: '0.65rem', fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>SNK</span>
            <code style={{ fontSize: '0.75rem', color: '#ef4444' }}>
              {edges[edges.length - 1]?.callee || edges[edges.length - 1]?.caller}
            </code>
            <span style={{
              fontSize: '0.65rem', fontWeight: 700, color: '#ef4444',
              background: '#ef444415', padding: '1px 6px', borderRadius: 4,
            }}>VULNERABLE</span>
          </div>
        )}
      </div>
    </div>
  );
}

function FindingCard({ item }) {
  const vpa = item.vulnerablePathAnalysis?.length ? item.vulnerablePathAnalysis : item.callPath;
  const statusCfg = STATUS[item.reachability] || STATUS.under_investigation;

  return (
    <div className="ce-card" style={item.reachability === 'reachable' ? { background: 'rgba(239,68,68,0.04)', borderColor: 'rgba(239,68,68,0.3)' } : {}}>
      <div className="ce-card-body">
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 3 }}>
            {item.finding?.cveId || item.finding?.osvId || '—'}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--color-muted, #64748b)' }}>
            <code>{item.finding?.packageName || '—'}</code>
            {item.finding?.version ? ` • v${item.finding.version}` : ''}
            {item.finding?.purl ? <span style={{ marginLeft: 8, opacity: 0.6 }}>{item.finding.purl}</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <ReachabilityBadge status={item.reachability} />
          <span className="ce-tag">
            {item.confidence || 'unknown'} confidence
          </span>
          {item.analysisTier && <TierBadge tier={item.analysisTier} />}
          {item.analyzer && (
            <span className="ce-sev ce-sev--info">{item.analyzer}</span>
          )}
        </div>
      </div>

      {/* Fix commit link */}
      {item.fixCommitUrl && (
        <div style={{ marginTop: 8, fontSize: '0.75rem' }}>
          <span style={{ color: 'var(--color-muted, #64748b)' }}>Fix commit: </span>
          <a href={item.fixCommitUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>
            {item.fixCommitUrl.replace('https://github.com/', '').replace('/commit/', '@').substring(0, 60)}
          </a>
        </div>
      )}

      {/* Vulnerable functions */}
      {item.vulnFunctions?.length > 0 && (
        <div style={{ marginTop: 8, fontSize: '0.75rem' }}>
          <span style={{ color: 'var(--color-muted, #64748b)' }}>Vulnerable functions: </span>
          {item.vulnFunctions.map((fn, i) => (
            <code key={i} style={{ marginRight: 8, fontSize: '0.72rem', color: '#f59e0b' }}>
              {fn.package ? `${fn.package}.` : ''}{fn.symbol}
            </code>
          ))}
        </div>
      )}

      {/* Vulnerable Path Analysis */}
      {vpa?.length > 0 && <VulnerablePathAnalysis edges={vpa} />}

      {/* Detail / reason */}
      {item.detail && (
        <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--color-muted, #64748b)', fontStyle: 'italic' }}>
          {item.detail}
        </div>
      )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const kpiColor = tone === 'danger' ? '#dc2626' : tone === 'success' ? '#16a34a' : tone === 'primary' ? '#6366f1' : undefined;
  return (
    <div className="col-6 col-lg-3">
      <div className="ce-kpi" style={kpiColor ? { '--kpi-color': kpiColor } : {}}>
        <div className="ce-kpi-value">{value}</div>
        <div className="ce-kpi-label">{label}</div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ReachabilityView({ repoName, sbomId, sbomName }) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [resolvedSbomName, setResolvedSbomName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [repoPath, setRepoPath] = useState('');
  const [persistVex, setPersistVex] = useState(true);
  const [filter, setFilter] = useState('all');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [scanComplete, setScanComplete] = useState(null); // {count, at}
  const scanStartRef = React.useRef(null);

  useEffect(() => {
    if (!repoName || !sbomId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    setData(null);
    setResolvedSbomName('');

    async function load() {
      const candidates = buildSbomCandidates(sbomId, sbomName);
      let saw404 = false;
      for (const candidate of candidates) {
        const response = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(candidate)}/reachability`);
        if (response.ok) {
          const payload = await response.json();
          if (!cancelled) { setData(payload); setResolvedSbomName(candidate); setRepoPath(payload.repoPath || ''); }
          return;
        }
        if (response.status === 404) { saw404 = true; continue; }
        throw new Error(`HTTP ${response.status}`);
      }
      if (!saw404) throw new Error('HTTP 404');
    }

    load()
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [repoName, sbomId, sbomName]);

  // ── Job polling ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    scanStartRef.current = Date.now();
    setElapsed(0);
    setScanComplete(null);

    // Elapsed timer
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - scanStartRef.current) / 1000));
    }, 1000);

    const targetSbom = resolvedSbomName || (sbomName?.endsWith('.json') ? sbomName : `${sbomId}-sbom.json`);

    const poll = async () => {
      try {
        const res = await authFetch(`${config.BASE_URL}/jobs/${jobId}`);
        if (!res.ok || cancelled) return;
        const job = await res.json();
        if (!cancelled) setJobStatus(job);

        if (job.status === 'done') {
          clearInterval(timer);
          // Fetch fresh results
          const r = await authFetch(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(targetSbom)}/reachability`);
          if (r.ok && !cancelled) {
            const payload = await r.json();
            const count = payload?.result?.findings?.length || 0;
            setData(payload);
            setResolvedSbomName(targetSbom);
            setScanComplete({ count, at: new Date() });
            showToast(`✅ Scan complete — ${count} vulnerabilities analyzed`, 'success');
          } else if (!cancelled) {
            showToast('⚠️ Scan completed but results could not be loaded. Try refreshing.', 'warning');
          }
          if (!cancelled) { setJobId(null); setScanning(false); setJobStatus(null); }

        } else if (job.status === 'failed') {
          clearInterval(timer);
          const msg = job.error || 'Scan failed';
          if (!cancelled) {
            setScanError(msg);
            showToast(`❌ Scan failed: ${msg}`, 'error');
            setJobId(null); setScanning(false); setJobStatus(null);
          }
        }
      } catch (_) {}
    };

    poll();
    const pollInterval = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(timer); clearInterval(pollInterval); };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = data?.result;
  const allFindings = useMemo(() => result?.findings || [], [result]);

  const summary = useMemo(() => {
    const s = { total: allFindings.length, reachable: 0, notAffected: 0, investigating: 0 };
    allFindings.forEach((item) => {
      if (item.reachability === 'reachable') s.reachable++;
      else if (item.reachability === 'not_affected') s.notAffected++;
      else s.investigating++;
    });
    return s;
  }, [allFindings]);

  const findings = useMemo(() => {
    if (filter === 'all') return allFindings;
    if (filter === 'reachable') return allFindings.filter(f => f.reachability === 'reachable');
    if (filter === 'not_affected') return allFindings.filter(f => f.reachability === 'not_affected');
    return allFindings.filter(f => f.reachability !== 'reachable' && f.reachability !== 'not_affected');
  }, [allFindings, filter]);

  const triggerScan = async () => {
    const targetSbom = resolvedSbomName || (sbomName?.endsWith('.json') ? sbomName : `${sbomId}-sbom.json`);
    if (!repoPath.trim()) { setScanError(t('reachability.repoPathRequired')); return; }
    setScanning(true); setScanError(''); setJobStatus(null);
    try {
      const response = await axios.post(
        `${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(targetSbom)}/reachability`,
        { repoPath: repoPath.trim(), persistVex }
      );
      // Backend returns 202 Accepted with {jobId} — start polling
      if (response.data?.jobId) {
        setJobId(response.data.jobId);
        setJobStatus({ status: 'running', progress: 5, message: 'Starting…' });
      } else {
        // Fallback: sync response (legacy)
        setData(response.data);
        setResolvedSbomName(targetSbom);
        setScanning(false);
      }
    } catch (e) {
      setScanError(e.response?.data?.error || e.message);
      setScanning(false);
    }
  };

  if (loading) return (
    <div style={{ padding: '1rem 0' }}>
      <Skeleton.Card />
      <div className="small text-secondary mt-3">{t('reachability.loading')}</div>
    </div>
  );

  if (error) return <div className="alert alert-danger">{t('reachability.loadError')}: {error}</div>;

  return (
    <div className="d-flex flex-column gap-4">

      {/* Scan trigger card */}
      <div className="ce-card">
        <div className="ce-card-body">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <span className="ce-card-title">🧭 {t('reachability.title')}</span>
              <div className="small text-secondary">{t('reachability.subtitle')}</div>
            </div>
            {result && (
              <div style={{ fontSize: '0.72rem', color: 'var(--color-muted, #64748b)' }}>
                Last scan: {result.analyzedAt ? new Date(result.analyzedAt).toLocaleString() : '—'}
              </div>
            )}
          </div>

          {/* Tier legend */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12, marginBottom: 4 }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--color-muted, #64748b)', alignSelf: 'center' }}>Analysis tiers:</span>
            {Object.entries(TIER).filter(([k]) => k !== 'unknown').map(([k, v]) => (
              <span key={k} title={v.title} style={{
                fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20,
                background: v.color + '18', color: v.color, border: `1px solid ${v.color}40`, cursor: 'help',
              }}>
                {v.label}
              </span>
            ))}
          </div>

          <div className="row g-3 mt-1">
            <div className="col-12 col-lg-8">
              <label className="form-label small fw-semibold">{t('reachability.repoPathLabel')}</label>
              <input className="form-control" value={repoPath} onChange={(e) => setRepoPath(e.target.value)}
                placeholder={t('reachability.repoPathPlaceholder')} />
              <div className="form-text">{t('reachability.repoPathHint')}</div>
            </div>
            <div className="col-12 col-lg-4 d-flex align-items-end pb-2">
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="persist-vex"
                  checked={persistVex} onChange={(e) => setPersistVex(e.target.checked)} />
                <label className="form-check-label small" htmlFor="persist-vex">
                  {t('reachability.persistVex')}
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 flex-wrap mt-3">
            <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={triggerScan} disabled={scanning || !sbomId}>
              {scanning ? '⏳ Running…' : t('reachability.runScan')}
            </button>
          </div>

          {/* ── Async scan progress ─────────────────────────────────────────── */}
          {scanning && jobStatus && (
            <div style={{
              marginTop: 16, padding: '16px 20px', borderRadius: 10,
              background: '#f0f7ff', border: '1px solid #bfdbfe',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e40af' }}>
                  🔬 Reachability Analysis in Progress
                </span>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.floor(elapsed / 60) > 0 ? `${Math.floor(elapsed / 60)}m ` : ''}{elapsed % 60}s elapsed
                </span>
              </div>

              {/* Step indicator */}
              {(() => {
                const progress = jobStatus.progress || 0;
                const steps = [
                  { label: 'Job created',             done: progress >= 5,  active: progress < 10 },
                  { label: 'Building code graph (CPG)', done: progress >= 85, active: progress >= 10 && progress < 85 },
                  { label: 'Analyzing taint flows',   done: progress >= 90, active: progress >= 85 && progress < 90 },
                  { label: 'Saving results',          done: progress >= 100,active: progress >= 90 && progress < 100 },
                ];
                return (
                  <div style={{ display: 'flex', gap: 0, marginBottom: 14 }}>
                    {steps.map((step, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                        {/* Connector line */}
                        {i > 0 && (
                          <div style={{
                            position: 'absolute', top: 10, left: '-50%', right: '50%', height: 2,
                            background: steps[i - 1].done ? '#3b82f6' : '#e2e8f0', zIndex: 0,
                          }} />
                        )}
                        {/* Dot */}
                        <div style={{
                          width: 20, height: 20, borderRadius: '50%', zIndex: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.6rem', fontWeight: 700,
                          background: step.done ? '#3b82f6' : step.active ? '#93c5fd' : '#e2e8f0',
                          color: step.done || step.active ? '#fff' : '#94a3b8',
                          border: step.active ? '2px solid #3b82f6' : '2px solid transparent',
                          boxShadow: step.active ? '0 0 0 3px #bfdbfe' : 'none',
                          transition: 'all 0.3s ease',
                        }}>
                          {step.done ? '✓' : i + 1}
                        </div>
                        <div style={{
                          marginTop: 5, fontSize: '0.65rem', textAlign: 'center',
                          color: step.done ? '#1d4ed8' : step.active ? '#3b82f6' : '#94a3b8',
                          fontWeight: step.active ? 700 : 400, maxWidth: 80,
                        }}>
                          {step.label}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Progress bar */}
              <div style={{ background: '#dbeafe', borderRadius: 6, height: 6, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{
                  width: `${jobStatus.progress || 5}%`, height: '100%',
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  borderRadius: 6, transition: 'width 0.5s ease',
                }} />
              </div>

              {/* Current message */}
              <div style={{ fontSize: '0.8rem', color: '#1e40af', marginBottom: 8 }}>
                {jobStatus.message || 'Processing…'}
              </div>

              {/* First-run warning (shown when still in CPG build phase) */}
              {(jobStatus.progress || 0) < 85 && (
                <div style={{
                  display: 'flex', gap: 8, padding: '8px 12px', borderRadius: 6,
                  background: '#fffbeb', border: '1px solid #fcd34d', alignItems: 'flex-start',
                }}>
                  <span style={{ fontSize: '1rem', flexShrink: 0 }}>⏱️</span>
                  <div style={{ fontSize: '0.75rem', color: '#92400e' }}>
                    <strong>First-time analysis:</strong> Building a Code Property Graph (CPG) for this repository.
                    This takes <strong>10–20 minutes</strong> depending on repo size — but the result is cached.
                    All subsequent scans will complete in seconds.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Scan error */}
          {scanError && <div className="alert alert-danger mt-3 mb-0">{scanError}</div>}

          {/* ── Scan complete banner ─────────────────────────────────────────── */}
          {scanComplete && !scanning && (
            <div style={{
              marginTop: 12, padding: '10px 16px', borderRadius: 8,
              background: '#f0fdf4', border: '1px solid #86efac',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: '1.1rem' }}>✅</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 700, color: '#166534', fontSize: '0.85rem' }}>
                  Scan complete
                </span>
                <span style={{ color: '#15803d', fontSize: '0.8rem', marginLeft: 8 }}>
                  {scanComplete.count} vulnerabilities analyzed · results updated below
                </span>
              </div>
              <button onClick={() => setScanComplete(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '1rem' }}>
                ×
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      {!result ? (
        <EmptyState icon="🧭" title={t('reachability.emptyTitle')} description={t('reachability.emptyDescription')} />
      ) : (
        <>
          {/* Summary cards */}
          <div className="row g-3">
            <SummaryCard label={t('reachability.summary.total')} value={summary.total} tone="secondary" />
            <SummaryCard label={t('reachability.summary.reachable')} value={summary.reachable} tone="danger" />
            <SummaryCard label={t('reachability.summary.notAffected')} value={summary.notAffected} tone="success" />
            <SummaryCard label={t('reachability.summary.investigating')} value={summary.investigating} tone="secondary" />
          </div>

          {/* Findings */}
          <div className="ce-card">
            <div className="ce-card-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                <div>
                  <h6 className="fw-semibold mb-1">{t('reachability.resultsTitle')}</h6>
                  <div className="small text-secondary">
                    {t('reachability.languages')}: {(result.languages || []).join(', ') || '—'}
                  </div>
                </div>
                {/* Filter tabs */}
                <div className="ce-filter-bar">
                  {[
                    { key: 'all', label: `All (${summary.total})` },
                    { key: 'reachable', label: `⚠️ Reachable (${summary.reachable})` },
                    { key: 'not_affected', label: `✅ Not affected (${summary.notAffected})` },
                    { key: 'under_investigation', label: `🔵 Investigating (${summary.investigating})` },
                  ].map(f => (
                    <button key={f.key}
                      onClick={() => setFilter(f.key)}
                      className={`ce-btn ce-btn--sm ${filter === f.key ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                    >{f.label}</button>
                  ))}
                </div>
              </div>

              {findings.length === 0 ? (
                <div className="text-secondary small">{t('reachability.noFindings')}</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {findings.map((item, idx) => (
                    <FindingCard key={item.finding?.cveId || item.finding?.osvId || idx} item={item} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
